import React from 'react';
import { Helmet } from 'react-helmet';
import { Shield, Cookie, Eye, Database, Settings } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const CookiesPolicy = () => {
  return (
    <div className="min-h-screen bg-primary text-tertiary">
      <Helmet>
        <title>Cookies Policy - Oracle Gaming</title>
        <meta name="description" content="Learn about how Oracle Gaming uses cookies to enhance your browsing experience." />
      </Helmet>
      
      <Header />
      <div className="h-20" />
      
      {/* Hero Section */}
      <section className="gallery-carousel" style={{ paddingTop: '120px', paddingBottom: '60px' }}>
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full border-4 border-orange flex items-center justify-center bg-black/50">
                <Cookie className="w-10 h-10 text-orange" />
              </div>
            </div>
            <h1 className="h1 section-title mb-4" style={{ color: 'var(--white)' }}>
              Cookies Policy
            </h1>
            <p className="about-text text-lg" style={{ color: 'var(--platinum)' }}>
              Understanding how we use cookies to improve your gaming experience
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section style={{ padding: '40px 0 80px 0', backgroundColor: '#1b1c21' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            
            {/* What Are Cookies */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <Cookie className="w-6 h-6 text-orange" />
                <h2 className="h2" style={{ color: 'var(--white)' }}>What Are Cookies?</h2>
              </div>
              <div className="bg-primarySupport p-6 rounded-lg border border-black/20">
                <p className="about-text mb-4">
                  Cookies are small text files that are placed on your computer or mobile device when you visit our website. 
                  They help us provide you with a better browsing experience by remembering your preferences and understanding 
                  how you use our site.
                </p>
                <p className="about-text">
                  At Oracle Gaming, we use cookies responsibly and in compliance with GDPR regulations to enhance your 
                  gaming experience while protecting your privacy.
                </p>
              </div>
            </div>

            {/* Types of Cookies */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <Settings className="w-6 h-6 text-orange" />
                <h2 className="h2" style={{ color: 'var(--white)' }}>Types of Cookies We Use</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Essential Cookies */}
                <div className="bg-primarySupport p-6 rounded-lg border border-black/20">
                  <h3 className="h3 mb-3" style={{ color: 'var(--orange)' }}>Essential Cookies</h3>
                  <p className="about-text mb-3">
                    These cookies are necessary for the website to function properly. They enable basic functions like 
                    page navigation, access to secure areas, and shopping cart functionality.
                  </p>
                  <ul className="about-text text-sm space-y-1">
                    <li>• Shopping cart and checkout process</li>
                    <li>• User authentication</li>
                    <li>• Security features</li>
                    <li>• Cookie consent preferences</li>
                  </ul>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-primarySupport p-6 rounded-lg border border-black/20">
                  <h3 className="h3 mb-3" style={{ color: 'var(--orange)' }}>Analytics Cookies</h3>
                  <p className="about-text mb-3">
                    These cookies help us understand how visitors interact with our website by collecting and reporting 
                    information anonymously.
                  </p>
                  <ul className="about-text text-sm space-y-1">
                    <li>• Page views and user behavior</li>
                    <li>• Popular products and features</li>
                    <li>• Website performance metrics</li>
                    <li>• Error tracking and debugging</li>
                  </ul>
                </div>

                {/* Functional Cookies */}
                <div className="bg-primarySupport p-6 rounded-lg border border-black/20">
                  <h3 className="h3 mb-3" style={{ color: 'var(--orange)' }}>Functional Cookies</h3>
                  <p className="about-text mb-3">
                    These cookies enable enhanced functionality and personalization, such as remembering your 
                    preferences and providing improved features.
                  </p>
                  <ul className="about-text text-sm space-y-1">
                    <li>• Language and region preferences</li>
                    <li>• Recently viewed products</li>
                    <li>• Wishlist and favorites</li>
                    <li>• Customized content</li>
                  </ul>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-primarySupport p-6 rounded-lg border border-black/20">
                  <h3 className="h3 mb-3" style={{ color: 'var(--orange)' }}>Marketing Cookies</h3>
                  <p className="about-text mb-3">
                    These cookies are used to deliver advertisements that are more relevant to you and your interests.
                  </p>
                  <ul className="about-text text-sm space-y-1">
                    <li>• Personalized product recommendations</li>
                    <li>• Social media integration</li>
                    <li>• Targeted advertising</li>
                    <li>• Campaign effectiveness tracking</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-orange" />
                <h2 className="h2" style={{ color: 'var(--white)' }}>Your Rights & Choices</h2>
              </div>
              <div className="bg-primarySupport p-6 rounded-lg border border-black/20">
                <p className="about-text mb-4">
                  You have full control over your cookie preferences. You can:
                </p>
                <ul className="about-text space-y-2">
                  <li>• <strong>Accept All:</strong> Allow all cookies for the best browsing experience</li>
                  <li>• <strong>Decline All:</strong> Reject non-essential cookies (some features may not work)</li>
                  <li>• <strong>Manage Preferences:</strong> Choose which types of cookies to allow</li>
                  <li>• <strong>Withdraw Consent:</strong> Change your mind at any time</li>
                  <li>• <strong>Delete Cookies:</strong> Clear existing cookies from your browser</li>
                </ul>
              </div>
            </div>

            {/* Third Party Services */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <Database className="w-6 h-6 text-orange" />
                <h2 className="h2" style={{ color: 'var(--white)' }}>Third-Party Services</h2>
              </div>
              <div className="bg-primarySupport p-6 rounded-lg border border-black/20">
                <p className="about-text mb-4">
                  We work with trusted third-party services that may set their own cookies:
                </p>
                <ul className="about-text space-y-2">
                  <li>• <strong>PayFast:</strong> Secure payment processing</li>
                  <li>• <strong>Google Analytics:</strong> Website analytics and insights</li>
                  <li>• <strong>Social Media:</strong> Facebook, Instagram integration</li>
                  <li>• <strong>WooCommerce:</strong> E-commerce functionality</li>
                </ul>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <Eye className="w-6 h-6 text-orange" />
                <h2 className="h2" style={{ color: 'var(--white)' }}>Questions or Concerns?</h2>
              </div>
              <div className="bg-primarySupport p-6 rounded-lg border border-black/20">
                <p className="about-text mb-4">
                  If you have any questions about our use of cookies or this policy, please don't hesitate to contact us:
                </p>
                <div className="about-text space-y-2">
                  <p>📧 Email: <a href="mailto:oraclegaming.za@gmail.com" className="text-orange hover:text-orange-400 transition-colors">oraclegaming.za@gmail.com</a></p>
                  <p>📱 Phone: <a href="tel:+27123456789" className="text-orange hover:text-orange-400 transition-colors">+27 12 345 6789</a></p>
                  <p>🌐 Website: <a href="https://oraclegaming.co.za" className="text-orange hover:text-orange-400 transition-colors">oraclegaming.co.za</a></p>
                </div>
              </div>
            </div>

            {/* Last Updated */}
            <div className="text-center">
              <p className="about-text text-sm" style={{ color: 'var(--platinum)' }}>
                This cookies policy was last updated on {new Date().toLocaleDateString('en-ZA', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiesPolicy;
